import { useTranslate } from "ra-core";
import type { ReactNode } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { OpcionFormErrors, OpcionFormValues } from "./opcionRules";

export const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base disabled:opacity-60";

const COUNT_FIELD_KEYS = [
  "levels",
  "bedrooms",
  "bathrooms",
  "area_m2",
  "parking",
] as const;

/** A labelled form control with its validation message. */
export const Field = ({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error ? (
      <p id={`${id}-error`} className="text-sm text-destructive">
        {error}
      </p>
    ) : null}
  </div>
);

/**
 * Almikia Epic EC2d (TASK-137): origin (with the exclusiva contract dates), operation (with the
 * furnished choice of a rental) and the physical specifications of the property form.
 */
export const OpcionSpecFields = ({
  idPrefix,
  values,
  errors,
  disabled,
  set,
}: {
  idPrefix: string;
  values: OpcionFormValues;
  errors: OpcionFormErrors;
  disabled: boolean;
  set: (key: keyof OpcionFormValues, value: string) => void;
}) => {
  const translate = useTranslate();
  const label = (key: string) =>
    translate(`resources.property_options.fields.${key}`);
  return (
    <>
      <Field id={`${idPrefix}-origin`} label={label("origin")}>
        <select
          id={`${idPrefix}-origin`}
          className={SELECT_CLASS}
          value={values.origin}
          disabled={disabled}
          onChange={(event) => set("origin", event.target.value)}
        >
          {(["opcion", "exclusiva"] as const).map((origin) => (
            <option key={origin} value={origin}>
              {translate(`crm.opciones.origin.${origin}`)}
            </option>
          ))}
        </select>
      </Field>
      {values.origin === "exclusiva" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id={`${idPrefix}-signed`}
            label={label("exclusivity_signed_on")}
            error={errors.exclusivity_signed_on}
          >
            <Input
              id={`${idPrefix}-signed`}
              type="date"
              className="h-11"
              value={values.exclusivity_signed_on}
              disabled={disabled}
              aria-invalid={errors.exclusivity_signed_on != null}
              onChange={(event) =>
                set("exclusivity_signed_on", event.target.value)
              }
            />
          </Field>
          <Field
            id={`${idPrefix}-ends`}
            label={label("exclusivity_ends_on")}
            error={errors.exclusivity_ends_on}
          >
            <Input
              id={`${idPrefix}-ends`}
              type="date"
              className="h-11"
              value={values.exclusivity_ends_on}
              disabled={disabled}
              aria-invalid={errors.exclusivity_ends_on != null}
              onChange={(event) =>
                set("exclusivity_ends_on", event.target.value)
              }
            />
          </Field>
        </div>
      ) : null}
      <Field id={`${idPrefix}-operation`} label={label("operation")}>
        <select
          id={`${idPrefix}-operation`}
          className={SELECT_CLASS}
          value={values.operation}
          disabled={disabled}
          onChange={(event) => set("operation", event.target.value)}
        >
          {(["venta", "renta"] as const).map((operation) => (
            <option key={operation} value={operation}>
              {translate(`crm.opciones.operation.${operation}`)}
            </option>
          ))}
        </select>
      </Field>
      {values.operation === "renta" ? (
        <Field
          id={`${idPrefix}-furnished`}
          label={label("furnished")}
          error={errors.furnished}
        >
          <select
            id={`${idPrefix}-furnished`}
            className={SELECT_CLASS}
            value={values.furnished}
            disabled={disabled}
            aria-invalid={errors.furnished != null}
            onChange={(event) => set("furnished", event.target.value)}
          >
            <option value="">
              {translate("crm.opciones.choose_furnished")}
            </option>
            {(["amueblado", "sin_amueblar"] as const).map((furnished) => (
              <option key={furnished} value={furnished}>
                {translate(`crm.opciones.furnished.${furnished}`)}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {COUNT_FIELD_KEYS.map((key) => (
          <Field
            key={key}
            id={`${idPrefix}-${key}`}
            label={label(key)}
            error={errors[key]}
          >
            <Input
              id={`${idPrefix}-${key}`}
              type="number"
              min={0}
              inputMode="decimal"
              className="h-11 tabular-nums"
              value={values[key]}
              disabled={disabled}
              aria-invalid={errors[key] != null}
              onChange={(event) => set(key, event.target.value)}
            />
          </Field>
        ))}
      </div>
    </>
  );
};
